"use client";

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

const CLOSE_MS = 220;

interface ModalContent {
  title?: string;
  body: ReactNode;
}

interface ModalValue {
  openModal: (content: ModalContent) => void;
  closeModal: () => void;
}

const ModalContext = createContext<ModalValue>({
  openModal: () => {},
  closeModal: () => {},
});

export const useModal = () => useContext(ModalContext);

export function ModalProvider({ children }: { children: ReactNode }) {
  const [content, setContent] = useState<ModalContent | null>(null);
  const [closing, setClosing] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const closeTimer = useRef<number | null>(null);
  const dragStart = useRef<{ mx: number; my: number; ox: number; oy: number } | null>(null);

  useEffect(() => setMounted(true), []);

  const closeModal = useCallback(() => {
    setClosing(true);
    closeTimer.current = window.setTimeout(() => {
      setContent(null);
      setClosing(false);
      closeTimer.current = null;
    }, CLOSE_MS);
  }, []);

  const openModal = useCallback((next: ModalContent) => {
    if (closeTimer.current !== null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setClosing(false);
    setOffset({ x: 0, y: 0 });
    setContent(next);
  }, []);

  // Перетаскивание окна за шапку
  const onHeadMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if ((e.target as HTMLElement).closest(".modal--close")) return;
      dragStart.current = {
        mx: e.clientX,
        my: e.clientY,
        ox: offset.x,
        oy: offset.y,
      };
      setDragging(true);
    },
    [offset]
  );

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e: MouseEvent) => {
      const d = dragStart.current;
      if (!d) return;
      setOffset({
        x: d.ox + e.clientX - d.mx,
        y: d.oy + e.clientY - d.my,
      });
    };
    const onUp = () => setDragging(false);
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, [dragging]);

  // Esc для закрытия + блокировка прокрутки фона
  useEffect(() => {
    if (!content) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeModal();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [content, closeModal]);

  return (
    <ModalContext.Provider value={{ openModal, closeModal }}>
      {children}
      {mounted &&
        content &&
        createPortal(
          <div
            className={`modal${closing ? " is-closing" : ""}${dragging ? " is-dragging" : ""}`}
            onClick={closeModal}
          >
            <div
              className="modal--window"
              style={{ transform: `translate(${offset.x}px, ${offset.y}px)` }}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
            >
              <div
                className="modal--head"
                onMouseDown={onHeadMouseDown}
              >
                {content.title && (
                  <h2 className="modal--title">{content.title}</h2>
                )}
                <button
                  className="modal--close"
                  type="button"
                  onClick={closeModal}
                  aria-label="Закрыть"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  >
                    <line x1="6" y1="6" x2="18" y2="18" />
                    <line x1="18" y1="6" x2="6" y2="18" />
                  </svg>
                </button>
              </div>
              <div className="modal--body">{content.body}</div>
            </div>
          </div>,
          document.body
        )}
    </ModalContext.Provider>
  );
}
